import React, { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ActivitiesService, UserService } from 'gw-capability-gateway';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLTagManagerService } from 'ml-portals-services-react'
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { InlineLoader } from '@jutro/components';

import metadata from './MLActivityDetailPopup.metadata.json5';
import styles from './MLActivityDetailPopup.module.scss';
import messages from './MLActivityDetailPopup.messages';

const MLActivityDetailPopup = (props) => {
    const {
        activity,
        currentUser,
        openAddNoteDialog,
        onCancel,
        onSave,
        viewModelService,
        authHeader,
        filteredJobs: items,
        updateActivities,
        isNewActivity,
        
    } = props;
    const { refreshData } = useDataRefresh();
    const translator = useTranslator();

    const { onValidate, isComponentValid } = useValidation('MLActivityDetailPopup');

    const noteTopicTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('NoteTopicType');
    const priorityTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('Priority');
    const activityStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('ActivityStatus');

    const initialActivityVM = viewModelService.create(activity, 'pc', 'metlife.edge.capabilities.gateway.activity.dto.ActivitySummaryDTOML');

    const [activityVM] = useState(initialActivityVM);
    const [uiReadonly, updateUiReadonly] = useState(false);
    const [activityCompleteLoading, setActivityCompleteLoading] = useState(false);
    const isCompleted = _.get(activityVM, 'status.value.code') === 'complete';

    const onValueChange = (value, path) => {
        _.set(activityVM, `${path}.value`, value);
        refreshData();
    }

    const priorityAvailableValues = priorityTypelist.codes.map((priority) => {
        return translator({
            code: priority.code,
            name: translator({
                id: priority.name,
                defaultMessage: priority.name
            })
        });
    });

    const statusAvailableValues = activityStatusTypelist.codes.map((status) => {
        return translator({
            code: status.code,
            name: translator({
                id: status.name,
                defaultMessage: status.name
            })
        });
    });

    const flagUIReadonly = () => {
        if (activityVM.status.value !== 'open') {
            updateUiReadonly(true);
        }
    };

    const updateFilteredJobs = (response) => {
        for (const prop of Object.getOwnPropertyNames(response)) {
            activity[prop] = response[prop];
        }
        MLTagManagerService.sendFormCompleteEvent(`${MLTagManagerService.getFormName()}_${MLTagManagerService.getStepName()}_Edit Follow Up`);
        const ind = _.findIndex(items, (act) => {
            return act.publicID === response.publicID
        });
        if (ind > -1) {
            items[ind] = response;
        }
        updateActivities(items);
    }

    const markActivityAsCompleted = () => {
        setActivityCompleteLoading(true);
        ActivitiesService.markActivityAsCompleted(activity.publicID, authHeader)
            .then((completeActivity) => {
                onValueChange(completeActivity.status, 'status')
                flagUIReadonly();
                updateFilteredJobs(completeActivity)
            }).finally(() => {
                setActivityCompleteLoading(false);
            })
    };

    const renderActivityCompleteLoader = (isLoading) => {
        return isLoading ? <><span>MARK ACTIVITY COMPLETE</span><InlineLoader loading={isLoading} className="gw-inline-loader" /></> : null;
    };

    const isActivityReadonly = () => {
        return !isNewActivity && (!(activity.isCreatedByCurrentUser && activity.isAssignedToCurrentUser) || uiReadonly);
    };

    const assignedNameToDisplay = () => {
        return _.isObject(activity.assignedTo) ? activity.assignedTo.displayName : activity.assignedTo;
    }

    const initiatorNameToDisplay = () => {
        return _.isObject(activity.assignedBy) ? activity.assignedBy.displayName : activity.assignedBy;
    }

    const getDescription = (note) => {
        return note.subject;
    }

    const getFullDescription = (note) => {
        return note.body;
    }

    const getCategory = (note) => {
        if (note.topic) {
            const noteName = noteTopicTypelist.getCode(note.topic).name;
            const noteCategory = translator({
                id: noteName,
                defaultMessage: noteName
            });
            return noteCategory;
        }
        return '-';
    };

    const onAddANoteClicked = async () => {
        const note = {
            createdDate: new Date(), // Today
            policyNumber_ML: activity.policyNumber,
            topic: 'servicing_ml'
        };
        if (!currentUser) {
            const userResp = await UserService.getCurrentUser(authHeader)
            note.authorName = userResp.displayName;
            const noteVM = viewModelService.create(note, 'pc', 'edge.capabilities.gateway.note.dto.NoteDTO');
            openAddNoteDialog(noteVM);
        } else {
            note.authorName = currentUser.displayName;
            const noteVM = viewModelService.create(note, 'pc', 'edge.capabilities.gateway.note.dto.NoteDTO');

            openAddNoteDialog(noteVM);
        }
    };

    const updateActivity = () => {
        if (!isComponentValid || !activityVM.aspects.valid || !activityVM.aspects.subtreeValid) {
            return;
        }
        ActivitiesService.updateActivitySummary(activityVM.value, authHeader)
            .then((response) => {
                updateFilteredJobs(response);
            });

        if (onSave) {
            onSave();
        }
    };

    const overrideProps = {
        markCompleteButton: {
            onClick: markActivityAsCompleted,
            disabled: isCompleted || (activity.canComplete_ML !== undefined && !activity.canComplete_ML) || activityCompleteLoading,
            content: renderActivityCompleteLoader(activityCompleteLoading)
        },
        activityDueDate: {
            readOnly: isActivityReadonly()
        },
        activitySubject: {
            readOnly: isActivityReadonly()
        },
        activityDesc: {
            readOnly: isActivityReadonly()
        },
        status: {
            readOnly: isActivityReadonly(),
            value: _.get(activityVM, 'status.value'),
            availableValues: statusAvailableValues
        },
        priority: {
            readOnly: isActivityReadonly(),
            value: _.get(activityVM, 'priority.value'),
            availableValues: priorityAvailableValues
        },
        initiator: {
            readOnly: true,
            value: initiatorNameToDisplay()
        },
        assignedTo: {
            readOnly: true,
            value: assignedNameToDisplay()
        },
        notesTable: {
            title: translator(messages.notes),
            data: activity.notes
        },
        addANoteButton: {
            onClick: onAddANoteClicked,
            disabled: isCompleted
        },
        saveButton: {
            visible: !isActivityReadonly()
        },
        closeButton: {
            content: !isActivityReadonly() ? translator(messages.cancel) : translator(messages.closeModal)
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            getDescription,
            getFullDescription,
            getCategory,
            onClose: onCancel,
            updateActivity
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={activityVM}
            onValueChange={onValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            onValidationChange={onValidate}
        />
    );
};

MLActivityDetailPopup.propTypes = {
    activityVM: PropTypes.shape({}).isRequired,
    currentUser: PropTypes.shape({}),
    openAddNoteDialog: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isNewActivity: PropTypes.bool
};

MLActivityDetailPopup.defaultProps = {
    currentUser: undefined,
    isNewActivity: false
};

export default MLActivityDetailPopup;
